<template>
  <div class="wizard-step-rail" role="list" :aria-label="ariaLabel">
    <template v-for="(step, index) in steps" :key="step.key ?? index">
      <div
        class="wizard-step-rail__step"
        role="listitem"
        :aria-label="`${stepLabel} ${index + 1}: ${step.title}`"
        :aria-current="index === currentIndex ? 'step' : undefined"
      >
        <div
          class="wizard-step-rail__indicator"
          :class="{
            'is-active': index === currentIndex,
            'is-done': index < currentIndex,
          }"
        >
          <FontAwesomeIcon
            v-if="index < currentIndex"
            icon="check"
            class="wizard-step-rail__check"
          />
          <span v-else aria-hidden="true">{{ index + 1 }}</span>
        </div>
        <div class="wizard-step-rail__meta">
          <div
            class="wizard-step-rail__eyebrow"
            :class="{
              'is-active': index === currentIndex,
              'is-done': index < currentIndex,
            }"
          >
            {{ stepLabel }} {{ index + 1 }}
          </div>
          <div
            class="wizard-step-rail__title"
            :class="{
              'is-active': index === currentIndex,
              'is-done': index < currentIndex,
            }"
          >
            {{ step.title }}
          </div>
        </div>
      </div>
      <div
        v-if="index < steps.length - 1"
        class="wizard-step-rail__connector"
        :class="{ 'is-done': index < currentIndex }"
        aria-hidden="true"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

export interface WizardStep {
  title: string;
  key?: string | number;
}

withDefaults(
  defineProps<{
    steps: WizardStep[];
    currentIndex: number;
    stepLabel?: string;
    ariaLabel?: string;
  }>(),
  { stepLabel: "Step", ariaLabel: "Wizard progress" }
);
</script>

<style scoped lang="scss">
.wizard-step-rail {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 16px 28px;
  border-bottom: 1px solid var(--phoenix-border-color-translucent);
  background: var(--phoenix-gray-50);
}

.wizard-step-rail__step {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
}

.wizard-step-rail__indicator {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 700;
  background: var(--phoenix-gray-200);
  color: var(--phoenix-gray-600);
  transition: all 0.15s ease;

  &.is-active {
    background: var(--phoenix-primary);
    color: #ffffff;
  }

  &.is-done {
    background: var(--phoenix-success-bg-subtle);
    color: var(--phoenix-success-text-emphasis);
  }
}

.wizard-step-rail__check {
  font-size: 11px;
}

.wizard-step-rail__meta {
  display: flex;
  flex-direction: column;
}

.wizard-step-rail__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--phoenix-gray-400);

  &.is-active {
    color: var(--phoenix-gray-500);
  }
}

.wizard-step-rail__title {
  font-size: 14px;
  font-weight: 700;
  color: var(--phoenix-gray-500);

  &.is-active {
    color: var(--phoenix-gray-1000);
  }

  &.is-done {
    color: var(--phoenix-gray-700);
  }
}

.wizard-step-rail__connector {
  flex: 1 1 auto;
  height: 2px;
  margin: 0 16px;
  background: var(--phoenix-gray-200);
  border-radius: 2px;
  transition: background 0.15s ease;

  &.is-done {
    background: var(--phoenix-success);
  }
}

@media (max-width: 768px) {
  .wizard-step-rail {
    padding: 12px 16px;
    gap: 8px;
  }

  .wizard-step-rail__connector {
    margin: 0 6px;
  }

  .wizard-step-rail__meta {
    display: none;
  }
}
</style>
